import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AgentResourcesService,
  DESCRIBE_BATCH_SIZE,
  type DescribeResourceItem,
  type DescribeResourceResult,
} from "@/services/agent/AgentResourcesService";
import type { InfoLink, ResourceDocument } from "@/types/agent/info-links";

/** Parallel requests; each carries up to DESCRIBE_BATCH_SIZE resources read in parallel server-side. */
const REQUEST_CONCURRENCY = 3;

export const linkDescribeItem = (link: InfoLink): DescribeResourceItem => ({
  id: link.id,
  kind: "link",
  label: link.label,
  url: link.url,
});

export const documentDescribeItem = (doc: ResourceDocument): DescribeResourceItem => ({
  id: doc.id,
  kind: "document",
  label: doc.label,
  url: doc.fileUrl,
  fileName: doc.fileName,
});

export const needsDescription = (resource: { description?: string | null }): boolean => !resource.description?.trim();

/**
 * Asks the AI to read resources and write their descriptions, in small batches
 * so results land row by row. Resources already queued are ignored, so the
 * auto-describe on upload and the manual "Describe with AI" can overlap safely.
 * Results are handed to `onResults` as each batch finishes.
 */
export const useDescribeResources = (
  agentId: string | undefined,
  onResults: (results: DescribeResourceResult[]) => void,
) => {
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const queuedRef = useRef(new Set<string>());
  const onResultsRef = useRef(onResults);
  onResultsRef.current = onResults;

  const settle = useCallback((ids: string[]) => {
    ids.forEach((id) => queuedRef.current.delete(id));
    setPendingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const describe = useCallback(
    async (items: DescribeResourceItem[]) => {
      const fresh = items.filter((item) => !queuedRef.current.has(item.id));
      if (!agentId || fresh.length === 0) return;

      fresh.forEach((item) => queuedRef.current.add(item.id));
      setPendingIds((prev) => new Set([...prev, ...fresh.map((item) => item.id)]));

      const batches: DescribeResourceItem[][] = [];
      for (let i = 0; i < fresh.length; i += DESCRIBE_BATCH_SIZE) batches.push(fresh.slice(i, i + DESCRIBE_BATCH_SIZE));

      let failures = 0;
      const worker = async () => {
        for (let batch = batches.shift(); batch; batch = batches.shift()) {
          try {
            const results = await AgentResourcesService.describe(agentId, batch);
            const described = results.filter((result) => result.description);
            failures += batch.length - described.length;
            if (described.length > 0) onResultsRef.current(described);
          } catch (error) {
            console.error("Describing resources failed", error);
            failures += batch.length;
          } finally {
            settle(batch.map((item) => item.id));
          }
        }
      };

      await Promise.all(Array.from({ length: Math.min(REQUEST_CONCURRENCY, batches.length) }, worker));

      if (failures > 0) {
        toast.error(
          failures === fresh.length
            ? "Couldn't describe these resources right now. Please try again."
            : `${failures} resource${failures === 1 ? "" : "s"} couldn't be described — you can write ${failures === 1 ? "it" : "them"} yourself.`,
        );
      }
    },
    [agentId, settle],
  );

  return { describe, pendingIds, isDescribing: pendingIds.size > 0 };
};
